import {
  AgentStatus,
  ClusterStatus,
  SecurityFeature,
  WorkloadOptimizationAgentStatus,
} from '@cast/types';

import { useGetOmniClusterFromList } from 'features/cluster/autoscaler/edge-locations/hooks';
import { useIsClusterSecurityAgentInstalled } from 'features/organization/security-insights/hooks/useIsClusterSecurityAgentInstalled';
import { useSingleClusterSettingsQuery } from 'hooks/queries/security-insights';
import { useWorkloadOptimizationAgentStatusQuery } from 'hooks/queries/workload-optimization';
import { Cluster } from 'types/cluster';
import { isClusterOmniEnabled } from 'utils/cluster';

import { ProductFeatures } from '../types';

import { isClusterManaged } from '@cast/shared-cluster-context-data-access';

type UseActiveFeaturesArgs = {
  cluster: Cluster;
  enabled: boolean;
  skipDemoMiddleware?: boolean;
};

export const useActiveFeatures = ({
  cluster,
  enabled,
  skipDemoMiddleware,
}: UseActiveFeaturesArgs): {
  isLoading: boolean;
  features: ProductFeatures;
} => {
  const isDiscoveredCluster = cluster.status === ClusterStatus.DISCOVERED;

  const {
    isInstalled: isSecurityAgentInstalled,
    isLoading: isSecurityInsightsAgentLoading,
  } = useIsClusterSecurityAgentInstalled({
    clusterId: cluster.id,
    enabled: enabled && !isDiscoveredCluster,
  });
  const { settings, isLoading: isSingleClusterSettingsLoading } =
    useSingleClusterSettingsQuery({
      clusterId: cluster.id,
      enabled:
        enabled &&
        !isDiscoveredCluster &&
        cluster.agentStatus === AgentStatus.ONLINE,
    });
  const { data, isLoading: isWorkloadOptimizationAgentStatusLoading } =
    useWorkloadOptimizationAgentStatusQuery({
      clusterId: cluster.id,
      enabled: enabled && !isDiscoveredCluster,
      skipDemoMiddleware,
    });

  const { clusterDetails, isLoading: isOmniLoading } =
    useGetOmniClusterFromList({
      clusterId: cluster.id,
      enabled: enabled && !isDiscoveredCluster,
    });

  const isWoopInstalled =
    data?.status === WorkloadOptimizationAgentStatus.RUNNING;

  return {
    isLoading:
      isSecurityInsightsAgentLoading ||
      isSingleClusterSettingsLoading ||
      isWorkloadOptimizationAgentStatusLoading ||
      isOmniLoading,
    features: {
      costMonitoring: true, // Cost monitoring is automatically enabled
      autoscaler: isClusterManaged(cluster),
      security: isSecurityAgentInstalled,
      networkCostMetrics: !!settings?.features?.includes(
        SecurityFeature.NETFLOW
      ),
      workloadAutoscaler: isWoopInstalled,
      gpuMetricsExporter: false, // TODO: (Alex) - to be implemented as part of UI-3586
      reliabilityMetrics: false,
      nvidiaGpuPlugin: false,
      omni: isClusterOmniEnabled(clusterDetails),
      kent: false,
    },
  };
};
